import { createSlice, SliceCaseReducers } from "@reduxjs/toolkit";
import { FC, useCallback, useEffect, useMemo } from "react";
import { LayoutConst } from "./layout.module";
import styled, {css, ThemeProvider, useTheme} from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {HintTooltip, useAdvancedLockBodyScroll, useBreakpoint, useIsMobile} from "@brg/ui";
import { CloseModalIcon } from "./CloseIconNew";
import { CloseModalIconMob } from "./CloseIconNewMob";
import { runModalCloseGuard } from "./modalCloseGuards";
import { AppTester } from "supporting";
import { tooptipStylesWhite } from "../../shared/constants/tooptipStyles";
import {useTranslation} from "react-i18next";
import {ApplicationTheme} from "../../shared/themes";

export interface ModalControllerSlice {
  name: string;
  args: Record<string, any>;
  prevModal?: Omit<ModalControllerSlice, "prevModal">;
  isConfirmPopUpOpen?: boolean;
}

const initialState: ModalControllerSlice = {
  name: "",
  args: {},
  prevModal: {
    name: "",
    args: {},
  },
  isConfirmPopUpOpen: false,
};

export const modalControllerSlice = createSlice<
  ModalControllerSlice,
  SliceCaseReducers<ModalControllerSlice>,
  "modalController"
>({
  name: "modalController",
  initialState,
  reducers: {
    showModal: (state, action) => {
      state.prevModal = {
        name: state.name,
        args: state.args,
      };
      state.args = action.payload.args ?? {};
      state.name = action.payload.name;
      state.isConfirmPopUpOpen = false;
    },
    hideModal: (state) => {
      state.name = "";
      state.args = {};
      state.prevModal = initialState.prevModal;
      state.isConfirmPopUpOpen = false;
    },
    backToPrevModal: (state) => {
      state.name = state.prevModal?.name ?? "";
      state.args = state.prevModal?.args ?? {};
    },
    updateArgs: (state, action) => {
      state.args = action.payload.args ?? {};
    },
    updatePrevModalArgs: (state, action) => {
      if (state.prevModal) {
        state.prevModal.args = {
          ...state.prevModal.args,
          ...action.payload,
        }
      }
    },
    setConfirmPopUpOpen: (state, action) => {
      state.isConfirmPopUpOpen = action.payload;
    },
  },
});

export interface ModalComponentOptions {
  fullscreen?: boolean;
  closable?: boolean;
  isPreviewer?: boolean;
  closeLeft?: boolean;
  component?: any;
  solidBackground?: boolean;
  importantBlack?: boolean;
  importantCustomBG?: string;
  headerVisibleMobile?: boolean;
  hideCloseOnMobile?: boolean;
  mobileVersion?: boolean;
  shadowClosableBackground?: boolean;
  shadowClosableBackgroundMobile?: boolean;
  mobileFlexStart?: boolean;
  mobileFlexEnd?: boolean;
  tabletFlexStart?: boolean;
  tabletFlexEnd?: boolean;
  containerId?: string;
  mobileSolidBackground?: boolean;
  customFillBackgroundColor?: string;
  customHoverCloserColor?: string;
  customZIndex?: number;
  bottomOffset?: number;//отступ снизу(например показать футер)
}

export const modalControllerActions = modalControllerSlice.actions;
export const modalControllerReduces = modalControllerSlice.reducer;

export const SHARED_MODALS: Record<string, ModalComponentOptions> = {};

// Селектор для контроллера модалок
export const modalControllerSelector = (store: {
  modalController: ModalControllerSlice;
}) => store.modalController;

/**
 * Хук для модалок
 */
export const useModal = () => {
  const dispatch = useDispatch();
  const show = useCallback(
    (name: string, props: any = {}) => {
      dispatch(modalControllerActions.showModal({ name, args: props ?? {} }));
    },
    [dispatch]
  );
  const hide = useCallback(() => {
    dispatch(modalControllerActions.hideModal({}));
  }, [dispatch]);
  const back = useCallback(() => {
    dispatch(modalControllerActions.backToPrevModal({}));
  }, [dispatch]);
  const register = useCallback((name: string, modal: ModalComponentOptions) => {
    SHARED_MODALS[name] = modal;
  }, []);
  const update = useCallback(
    (props: any) => {
      dispatch(modalControllerActions.updateArgs(props));
    },
    [dispatch]
  );
  const updatePrev = useCallback(
    (props: Record<string, unknown>) => {
      dispatch(modalControllerActions.updatePrevModalArgs(props));
    },
    [dispatch]
  );
  return { show, hide, register, update, updatePrev, back /*isOpen*/ };
};

export const ModalControllerContent = styled.div<{
  offsetTop?: boolean, shadowClosableBackground?: boolean,
  mobileFlexStart?: boolean, mobileFlexEnd?: boolean,
  tabletFlexStart?: boolean, tabletFlexEnd?: boolean,
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  // z-index: 1;

  @media (max-width: ${LayoutConst.screen.l_12}px) {
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    overflow-y: scroll;
  }

  @media(max-width: ${LayoutConst.screen.md}px) {
    padding-top: ${({ offsetTop }) => offsetTop ? '80px' : '0'};
    justify-content: ${(p) => (p?.tabletFlexStart ? "flex-start" : null)};
    justify-content: ${(p) => (p?.tabletFlexEnd ? "flex-end" : null)};
  }
  
  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    overflow: hidden;
    overflow-y: auto;
    justify-content: center;
    justify-content: ${(p) => (p?.mobileFlexStart ? "flex-start" : null)};
    justify-content: ${(p) => (p?.mobileFlexEnd ? "flex-end" : null)};
  }
`;

export const ModalControllerContainer = styled.div<ModalComponentOptions>`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  //max-width: 100vw;
  //max-height: 100vh;

  z-index: ${(p) => p.customZIndex || 200100};
  background: ${(p) =>
    p.customFillBackgroundColor
      ?? p.solidBackground
      ? p.theme.modal.solidBackgroundColor
      : p.theme.modal.backgroundColor};
  background: ${(p) => (p?.importantCustomBG ? p?.importantCustomBG : p.fullscreen ? "transparent" : p?.importantBlack ? 'rgb(52, 52, 52)' : p.customFillBackgroundColor)};
  backdrop-filter: ${(p) => (p.solidBackground ? null : "blur(24px)")};
  background: ${(p) => (p?.shadowClosableBackground ? "rgba(0,0,0,0.5)" : p.customFillBackgroundColor)};
  transition: ${(p) => (p?.shadowClosableBackground ? "all 0.3s" : "none")};
  user-select: none;

    // Учитываем отступ снизу
  ${(p) => p.bottomOffset && css`
    height: calc(100% - ${p.bottomOffset}px);
    bottom: 0;
  `}

  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-top: ${({ headerVisibleMobile }) =>
    headerVisibleMobile ? "48px" : 0};
    /* height: ${({ headerVisibleMobile }) =>
    headerVisibleMobile ? "calc(100% - 48px)" : "100%"}; */
    height: ${({ headerVisibleMobile, bottomOffset }) =>
        !headerVisibleMobile 
            ? "100%"
            : bottomOffset 
                ? `calc(100% - 48px + ${bottomOffset})`
                : "calc(100% - 48px)"
    };
    background: ${(p) =>
    p?.shadowClosableBackgroundMobile ? 'rgba(0,0,0,0.5)' : p.mobileSolidBackground
      ? p.theme.modal.solidBackgroundColor
      : p.customFillBackgroundColor};
    backdrop-filter: ${(p) => (p.mobileSolidBackground ? "none" : null)};
    
    *::-webkit-scrollbar {
      width: 6px; /* ширина scrollbar */
      height: 61px;
    }
    *::-webkit-scrollbar-track {
      background: transparent; /* цвет дорожки */
    }
    *::-webkit-scrollbar-thumb {
      border-radius: 3px; /* закругления плашки */
      background: #D9D9D9;
      border: 1.55px solid rgba(0, 0, 0, 0); /* имитация марджина */
      background-clip: padding-box; /* имитация марджина */
    }
  }
`;

export const ModalClose = styled.div<ModalComponentOptions>`
  cursor: pointer;
  z-index: 300100;
  position: absolute;
  top: ${({ mobileVersion }) => mobileVersion ? '12px' : '80px'};
  right: ${(p) => (p.closeLeft ? null : p?.mobileVersion ? '10px' : "80px")};
  left: ${(p) => (!p.closeLeft ? null : p?.mobileVersion ? '10px' : "80px")};
  transition: all 0.2s;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {

    top: 32px;
    right: 32px;
    svg {
      width: 56px;
      height: 56px; 
    }
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    top: 16px;
    right: ${(p) => (p.closeLeft ? null : "16px")};
    left: ${(p) => (!p.closeLeft ? null : "16px")};

    // svg {
    //   width: 40px;
    //   height: 40px;
    // }
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    top: 12px;
    right: ${(p) => (p.closeLeft ? null : "10px")};
    left: ${(p) => (!p.closeLeft ? null : "10px")};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  &:hover {
    svg path {
      fill: ${(p) => p.customHoverCloserColor || p.theme.modal.closeColor};
    }
  }
  
  ${(p) => p.isPreviewer && css`
    svg path {
      fill: #FEFEFE;
    }
    &:hover {
      svg path {
        fill: #FEFEFE;
      }
    }
  `}
`;

// The modal provider
export const ModalController: FC = (props) => {
  const modal = useSelector(modalControllerSelector);
  const dispatch = useDispatch();
  const isVisible = useMemo(() => modal.name !== "", [modal]);
  const isMobile = useIsMobile();
  const [lockBody, unlockBody] = useAdvancedLockBodyScroll()
  const isGeograffe = AppTester.isGeograffee();
  const isBooksGallllery = AppTester.isBooksGallllery();
  const isCGRAVE = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const { t } = useTranslation()
  const is1200 = useBreakpoint(1200)

  const theme = useTheme()

  const onClose = useCallback(() => {

    if (modal.name && runModalCloseGuard(modal.name) === false) {
      return
    }

    if (typeof (modal.args?.onCustomClose) === 'function') {
      modal.args?.onCustomClose()
      return
    }

    if (typeof (modal.args?.close) === 'function') modal.args.close()
    dispatch(modalControllerActions.hideModal({}))
  }, [modal.args, modal.name, dispatch])

  useEffect(() => {
    isVisible ? lockBody() : unlockBody();
  }, [isVisible]);

  if (isVisible) {
    const ModalData = SHARED_MODALS[modal.name];
    const {
      component: ModalComponent,
      closable,
      solidBackground,
      headerVisibleMobile,
      hideCloseOnMobile,
      mobileVersion,
      shadowClosableBackground,
      mobileFlexStart,
      mobileFlexEnd,
      mobileSolidBackground,
      importantBlack,
      tabletFlexStart,
      tabletFlexEnd,
      isPreviewer,
      customFillBackgroundColor,
      shadowClosableBackgroundMobile,
      customHoverCloserColor,
      bottomOffset,
      customZIndex
    } = ModalData;

    const shouldMinorIcon = mobileVersion && isMobile;

    return (
        <ThemeProvider theme={(isBooksGallllery ? ApplicationTheme.white : theme) as any}>
          <ModalControllerContainer
              mobileSolidBackground={isGeograffe || isCGRAVE || isAdcrDafesNet || mobileSolidBackground}
              solidBackground={(isGeograffe && modal.name !== "not-saved-data") || solidBackground || isAdcrDafesNet || isCGRAVE || (isMobile && ["tags", "terms-of-use"].includes(modal.name)) || shadowClosableBackground}
              shadowClosableBackground={shadowClosableBackground}
              shadowClosableBackgroundMobile={shadowClosableBackgroundMobile}
              headerVisibleMobile={headerVisibleMobile}
              importantBlack={importantBlack}
              customFillBackgroundColor={customFillBackgroundColor}
              bottomOffset={bottomOffset}
              customZIndex={customZIndex}
              onClick={() => shadowClosableBackground && onClose()}
          >
            {(closable === undefined || closable) &&
                !(isMobile && hideCloseOnMobile) && (!shadowClosableBackground || isPreviewer) && (!is1200 || modal.name !== "tags") && (
                    <ModalClose
                        closeLeft={false}
                        onClick={() => onClose()}
                        mobileVersion={shouldMinorIcon}
                        isPreviewer={isPreviewer}
                        customHoverCloserColor={customHoverCloserColor}
                    >
                      {/* &times; */}
                      {shouldMinorIcon ?
                          <CloseModalIconMob /> :
                          (["tags"].includes(modal.name)
                              ? <HintTooltip customStyle={tooptipStylesWhite} text={t("hints.close_without_saving")} z={6} closeToRightCorner>
                                <CloseModalIcon />
                              </HintTooltip>
                              : <CloseModalIcon />)
                      }
                    </ModalClose>
                )}
            <ModalControllerContent
                offsetTop={shouldMinorIcon}
                shadowClosableBackground={shadowClosableBackground}
                mobileFlexStart={mobileFlexStart}
                mobileFlexEnd={mobileFlexEnd}
                tabletFlexStart={tabletFlexStart}
                tabletFlexEnd={tabletFlexEnd}
            >
              {shadowClosableBackground ? <div onClick={(e: any) => { shadowClosableBackground && e.stopPropagation() }}>
                <ModalComponent {...modal.args} />
              </div> : <ModalComponent {...modal.args} />}
            </ModalControllerContent>
          </ModalControllerContainer>
        </ThemeProvider>
    );
  }

  return null;
};
import { createSlice, SliceCaseReducers } from "@reduxjs/toolkit";
import { FC, useCallback, useEffect, useMemo } from "react";
import { LayoutConst } from "./layout.module";
import styled, {css, ThemeProvider, useTheme} from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {HintTooltip, useAdvancedLockBodyScroll, useBreakpoint, useIsMobile} from "@brg/ui";
import { CloseModalIcon } from "./CloseIconNew";
import { CloseModalIconMob } from "./CloseIconNewMob";
import { runModalCloseGuard } from "./modalCloseGuards";
import { AppTester } from "supporting";
import { tooptipStylesWhite } from "../../shared/constants/tooptipStyles";
import {useTranslation} from "react-i18next";
import {ApplicationTheme} from "../../shared/themes";

export interface ModalControllerSlice {
  name: string;
  args: Record<string, any>;
  prevModal?: Omit<ModalControllerSlice, "prevModal">;
  isConfirmPopUpOpen?: boolean;
}

const initialState: ModalControllerSlice = {
  name: "",
  args: {},
  prevModal: {
    name: "",
    args: {},
  },
  isConfirmPopUpOpen: false,
};

export const modalControllerSlice = createSlice<
  ModalControllerSlice,
  SliceCaseReducers<ModalControllerSlice>,
  "modalController"
>({
  name: "modalController",
  initialState,
  reducers: {
    showModal: (state, action) => {
      state.prevModal = {
        name: state.name,
        args: state.args,
      };
      state.args = action.payload.args ?? {};
      state.name = action.payload.name;
      state.isConfirmPopUpOpen = false;
    },
    hideModal: (state) => {
      state.name = "";
      state.args = {};
      state.prevModal = initialState.prevModal;
      state.isConfirmPopUpOpen = false;
    },
    backToPrevModal: (state) => {
      state.name = state.prevModal?.name ?? "";
      state.args = state.prevModal?.args ?? {};
    },
    updateArgs: (state, action) => {
      state.args = action.payload.args ?? {};
    },
    updatePrevModalArgs: (state, action) => {
      if (state.prevModal) {
        state.prevModal.args = {
          ...state.prevModal.args,
          ...action.payload,
        }
      }
    },
    setConfirmPopUpOpen: (state, action) => {
      state.isConfirmPopUpOpen = action.payload;
    },
  },
});

export interface ModalComponentOptions {
  fullscreen?: boolean;
  closable?: boolean;
  isPreviewer?: boolean;
  closeLeft?: boolean;
  component?: any;
  solidBackground?: boolean;
  importantBlack?: boolean;
  importantCustomBG?: string;
  headerVisibleMobile?: boolean;
  hideCloseOnMobile?: boolean;
  mobileVersion?: boolean;
  shadowClosableBackground?: boolean;
  shadowClosableBackgroundMobile?: boolean;
  mobileFlexStart?: boolean;
  mobileFlexEnd?: boolean;
  tabletFlexStart?: boolean;
  tabletFlexEnd?: boolean;
  containerId?: string;
  mobileSolidBackground?: boolean;
  customFillBackgroundColor?: string;
  customHoverCloserColor?: string;
  customZIndex?: number;
  bottomOffset?: number;//отступ снизу(например показать футер)
}

export const modalControllerActions = modalControllerSlice.actions;
export const modalControllerReduces = modalControllerSlice.reducer;

export const SHARED_MODALS: Record<string, ModalComponentOptions> = {};

// Селектор для контроллера модалок
export const modalControllerSelector = (store: {
  modalController: ModalControllerSlice;
}) => store.modalController;

/**
 * Хук для модалок
 */
export const useModal = () => {
  const dispatch = useDispatch();
  const show = useCallback(
    (name: string, props: any = {}) => {
      dispatch(modalControllerActions.showModal({ name, args: props ?? {} }));
    },
    [dispatch]
  );
  const hide = useCallback(() => {
    dispatch(modalControllerActions.hideModal({}));
  }, [dispatch]);
  const back = useCallback(() => {
    dispatch(modalControllerActions.backToPrevModal({}));
  }, [dispatch]);
  const register = useCallback((name: string, modal: ModalComponentOptions) => {
    SHARED_MODALS[name] = modal;
  }, []);
  const update = useCallback(
    (props: any) => {
      dispatch(modalControllerActions.updateArgs(props));
    },
    [dispatch]
  );
  const updatePrev = useCallback(
    (props: Record<string, unknown>) => {
      dispatch(modalControllerActions.updatePrevModalArgs(props));
    },
    [dispatch]
  );
  return { show, hide, register, update, updatePrev, back /*isOpen*/ };
};

export const ModalControllerContent = styled.div<{
  offsetTop?: boolean, shadowClosableBackground?: boolean,
  mobileFlexStart?: boolean, mobileFlexEnd?: boolean,
  tabletFlexStart?: boolean, tabletFlexEnd?: boolean,
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  // z-index: 1;

  @media (max-width: ${LayoutConst.screen.l_12}px) {
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    overflow-y: scroll;
  }

  @media(max-width: ${LayoutConst.screen.md}px) {
    padding-top: ${({ offsetTop }) => offsetTop ? '80px' : '0'};
    justify-content: ${(p) => (p?.tabletFlexStart ? "flex-start" : null)};
    justify-content: ${(p) => (p?.tabletFlexEnd ? "flex-end" : null)};
  }
  
  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    overflow: hidden;
    overflow-y: auto;
    justify-content: center;
    justify-content: ${(p) => (p?.mobileFlexStart ? "flex-start" : null)};
    justify-content: ${(p) => (p?.mobileFlexEnd ? "flex-end" : null)};
  }
`;

export const ModalControllerContainer = styled.div<ModalComponentOptions>`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  //max-width: 100vw;
  //max-height: 100vh;

  z-index: ${(p) => p.customZIndex || 200100};
  background: ${(p) =>
    p.customFillBackgroundColor
      ?? p.solidBackground
      ? p.theme.modal.solidBackgroundColor
      : p.theme.modal.backgroundColor};
  background: ${(p) => (p?.importantCustomBG ? p?.importantCustomBG : p.fullscreen ? "transparent" : p?.importantBlack ? 'rgb(52, 52, 52)' : p.customFillBackgroundColor)};
  backdrop-filter: ${(p) => (p.solidBackground ? null : "blur(24px)")};
  background: ${(p) => (p?.shadowClosableBackground ? "rgba(0,0,0,0.5)" : p.customFillBackgroundColor)};
  transition: ${(p) => (p?.shadowClosableBackground ? "all 0.3s" : "none")};
  user-select: none;

    // Учитываем отступ снизу
  ${(p) => p.bottomOffset && css`
    height: calc(100% - ${p.bottomOffset}px);
    bottom: 0;
  `}

  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-top: ${({ headerVisibleMobile }) =>
    headerVisibleMobile ? "48px" : 0};
    /* height: ${({ headerVisibleMobile }) =>
    headerVisibleMobile ? "calc(100% - 48px)" : "100%"}; */
    height: ${({ headerVisibleMobile, bottomOffset }) =>
        !headerVisibleMobile 
            ? "100%"
            : bottomOffset 
                ? `calc(100% - 48px + ${bottomOffset})`
                : "calc(100% - 48px)"
    };
    background: ${(p) =>
    p?.shadowClosableBackgroundMobile ? 'rgba(0,0,0,0.5)' : p.mobileSolidBackground
      ? p.theme.modal.solidBackgroundColor
      : p.customFillBackgroundColor};
    backdrop-filter: ${(p) => (p.mobileSolidBackground ? "none" : null)};
    
    *::-webkit-scrollbar {
      width: 6px; /* ширина scrollbar */
      height: 61px;
    }
    *::-webkit-scrollbar-track {
      background: transparent; /* цвет дорожки */
    }
    *::-webkit-scrollbar-thumb {
      border-radius: 3px; /* закругления плашки */
      background: #D9D9D9;
      border: 1.55px solid rgba(0, 0, 0, 0); /* имитация марджина */
      background-clip: padding-box; /* имитация марджина */
    }
  }
`;

export const ModalClose = styled.div<ModalComponentOptions>`
  cursor: pointer;
  z-index: 300100;
  position: absolute;
  top: ${({ mobileVersion }) => mobileVersion ? '12px' : '80px'};
  right: ${(p) => (p.closeLeft ? null : p?.mobileVersion ? '10px' : "80px")};
  left: ${(p) => (!p.closeLeft ? null : p?.mobileVersion ? '10px' : "80px")};
  transition: all 0.2s;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {

    top: 32px;
    right: 32px;
    svg {
      width: 56px;
      height: 56px; 
    }
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    top: 16px;
    right: ${(p) => (p.closeLeft ? null : "16px")};
    left: ${(p) => (!p.closeLeft ? null : "16px")};

    // svg {
    //   width: 40px;
    //   height: 40px;
    // }
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    top: 12px;
    right: ${(p) => (p.closeLeft ? null : "10px")};
    left: ${(p) => (!p.closeLeft ? null : "10px")};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  &:hover {
    svg path {
      fill: ${(p) => p.customHoverCloserColor || p.theme.modal.closeColor};
    }
  }
  
  ${(p) => p.isPreviewer && css`
    svg path {
      fill: #FEFEFE;
    }
    &:hover {
      svg path {
        fill: #FEFEFE;
      }
    }
  `}
`;

// The modal provider
export const ModalController: FC = (props) => {
  const modal = useSelector(modalControllerSelector);
  const dispatch = useDispatch();
  const isVisible = useMemo(() => modal.name !== "", [modal]);
  const isMobile = useIsMobile();
  const [lockBody, unlockBody] = useAdvancedLockBodyScroll()
  const isGeograffe = AppTester.isGeograffee();
  const isBooksGallllery = AppTester.isBooksGallllery();
  const isCGRAVE = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const { t } = useTranslation()
  const is1200 = useBreakpoint(1200)

  const theme = useTheme()

  const onClose = useCallback(() => {

    if (modal.name && runModalCloseGuard(modal.name) === false) {
      return
    }

    if (typeof (modal.args?.onCustomClose) === 'function') {
      modal.args?.onCustomClose()
      return
    }

    if (typeof (modal.args?.close) === 'function') modal.args.close()
    dispatch(modalControllerActions.hideModal({}))
  }, [modal.args, modal.name, dispatch])

  useEffect(() => {
    isVisible ? lockBody() : unlockBody();
  }, [isVisible]);

  if (isVisible) {
    const ModalData = SHARED_MODALS[modal.name];
    const {
      component: ModalComponent,
      closable,
      solidBackground,
      headerVisibleMobile,
      hideCloseOnMobile,
      mobileVersion,
      shadowClosableBackground,
      mobileFlexStart,
      mobileFlexEnd,
      mobileSolidBackground,
      importantBlack,
      tabletFlexStart,
      tabletFlexEnd,
      isPreviewer,
      customFillBackgroundColor,
      shadowClosableBackgroundMobile,
      customHoverCloserColor,
      bottomOffset,
      customZIndex
    } = ModalData;

    const shouldMinorIcon = mobileVersion && isMobile;

    return (
        <ThemeProvider theme={(isBooksGallllery ? ApplicationTheme.white : theme) as any}>
          <ModalControllerContainer
              mobileSolidBackground={isGeograffe || isCGRAVE || isAdcrDafesNet || mobileSolidBackground}
              solidBackground={(isGeograffe && modal.name !== "not-saved-data") || solidBackground || isAdcrDafesNet || isCGRAVE || (isMobile && ["tags", "terms-of-use"].includes(modal.name)) || shadowClosableBackground}
              shadowClosableBackground={shadowClosableBackground}
              shadowClosableBackgroundMobile={shadowClosableBackgroundMobile}
              headerVisibleMobile={headerVisibleMobile}
              importantBlack={importantBlack}
              customFillBackgroundColor={customFillBackgroundColor}
              bottomOffset={bottomOffset}
              customZIndex={customZIndex}
              onClick={() => shadowClosableBackground && onClose()}
          >
            {(closable === undefined || closable) &&
                !(isMobile && hideCloseOnMobile) && (!shadowClosableBackground || isPreviewer) && (!is1200 || modal.name !== "tags") && (
                    <ModalClose
                        closeLeft={false}
                        onClick={() => onClose()}
                        mobileVersion={shouldMinorIcon}
                        isPreviewer={isPreviewer}
                        customHoverCloserColor={customHoverCloserColor}
                    >
                      {/* &times; */}
                      {shouldMinorIcon ?
                          <CloseModalIconMob /> :
                          (["tags"].includes(modal.name)
                              ? <HintTooltip customStyle={tooptipStylesWhite} text={t("hints.close_without_saving")} z={6} closeToRightCorner>
                                <CloseModalIcon />
                              </HintTooltip>
                              : <CloseModalIcon />)
                      }
                    </ModalClose>
                )}
            <ModalControllerContent
                offsetTop={shouldMinorIcon}
                shadowClosableBackground={shadowClosableBackground}
                mobileFlexStart={mobileFlexStart}
                mobileFlexEnd={mobileFlexEnd}
                tabletFlexStart={tabletFlexStart}
                tabletFlexEnd={tabletFlexEnd}
            >
              {shadowClosableBackground ? <div onClick={(e: any) => { shadowClosableBackground && e.stopPropagation() }}>
                <ModalComponent {...modal.args} />
              </div> : <ModalComponent {...modal.args} />}
            </ModalControllerContent>
          </ModalControllerContainer>
        </ThemeProvider>
    );
  }

  return null;
};
